/* eslint-disable no-alert */

"use client";

import React from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import WistiaVideo, { CircularPlayButton } from "@/components/shared/wistia-video";
import {BasicInfo, BasicInfoCta, BasicInfoMultipleCtas} from "@/types/dto/common/basic-info";
import ReactMarkdown from "react-markdown";
import { Image as ImageType } from "@/types/dto/common/image";
import { FeefoSummary } from "@/types/dto/common/feefo-reviews";
import FeefoSummarySection from "@/components/landing-page/feefo-summary-section";
import {Box, Typography, Stack, SxProps, Theme} from "@mui/material";
import { TypographyVariant } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { resolveModuleRanking } from "@/utils/module-ranking-utils";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import dynamicCtaButtonListFactory, {
  dynamicCtaButtonFactory
} from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import {SectionHeader} from "../section-header";

export interface PageHeroSlotProps {
  heroHeader?: {
    sx?: SxProps<Theme>;
  };
  sectionHeader?: {
    copySx?: SxProps;
  };
  rootContainer?: {
    removePatternBg?: boolean;
  };
  contentContainer?: {
    sx?: SxProps<Theme>;
  };
  textContent?: {
    sx?: SxProps<Theme>;
  };
  innerTextContainer?: {
    sx?: SxProps<Theme>;
  };
  mediaContainer?: {
    sx?: SxProps<Theme>;
  };
}

export interface PageHeroProps {
  testId: string;
  heroHeader?: string;
  heroBasicInfo: BasicInfo | BasicInfoMultipleCtas;
  headerImage: ImageType;
  video?: {
    videoId: string;
    videoTitle: string;
  };
  useCircularPlayButton?: boolean;
  startMuted?: boolean;
  padding?: {
    xs?: string;
    mobile?: string;
    tablet?: string;
    desktop?: string;
  };
  isSmFeefoSummary?: boolean;
  feefoSummary?: FeefoSummary;
  moduleRankingPosition?: string;
  parentRanking?: string;
  mediaLeft?: boolean;
  headerLevel?: TypographyVariant;
  slotProps?: PageHeroSlotProps;
  dataModuleName?: string;
}

const PageHero: React.FC<PageHeroProps> = ({
  testId,
  heroHeader,
  heroBasicInfo,
  headerImage,
  video,
  useCircularPlayButton,
  padding,
  startMuted = false,
  isSmFeefoSummary,
  feefoSummary,
  moduleRankingPosition = "1",
  parentRanking,
  mediaLeft = false,
  headerLevel,
  slotProps,
  dataModuleName = "Hero Section"
}) => {
  const t = useTranslations("COMMON");
  const { headerText, copyText } = heroBasicInfo;
  const trimmedHeroHeader = heroHeader?.trim();
  const trimmedHeaderText = headerText?.trim();
  const hasHeroHeader = Boolean(trimmedHeroHeader);
  const primaryHeader = hasHeroHeader ? trimmedHeroHeader : trimmedHeaderText;
  const secondaryHeader = hasHeroHeader ? trimmedHeaderText : undefined;
  const hasMedia = !!(video || headerImage);
  let mediaContent = null;

  const renderCopyText = () => {
    if (!copyText) {
      return null;
    }

    if (typeof copyText === "string") {
      if (/<\/?[a-z][^>]*>/i.test(copyText)) {
        // eslint-disable-next-line react/no-danger
        return <div dangerouslySetInnerHTML={{ __html: copyText }} />;
      }

      return <ReactMarkdown>{copyText}</ReactMarkdown>;
    }

    // eslint-disable-next-line react/no-danger
    return <div dangerouslySetInnerHTML={{ __html: copyText as unknown as string }} />;
  };

  const listOfCtas: BasicInfoCta[] | undefined =
    "ctas" in heroBasicInfo && heroBasicInfo.ctas
      ? heroBasicInfo.ctas
      : undefined;

  const singleCtaBasicInfo: BasicInfo | undefined =
    !("ctas" in heroBasicInfo) || !heroBasicInfo.ctas
      ? heroBasicInfo
      : undefined;

  const singleDynamicCta = singleCtaBasicInfo?.callToActionText
    ? dynamicCtaButtonFactory(singleCtaBasicInfo, t)
    : null;

  const multipleDynamicCtas = listOfCtas?.length
    ? dynamicCtaButtonListFactory(listOfCtas, t)
    : null;

  // Resolve module ranking - if this hero is nested in another ranked component,
  // it will return undefined to prevent duplicate rankings
  const resolvedRanking = resolveModuleRanking(moduleRankingPosition, parentRanking);

  if (video?.videoId !== "" && video) {
    const { videoTitle, videoId } = video;
    mediaContent = (
      <WistiaVideo
        videoId={videoId}
        title={videoTitle}
        startMuted={startMuted}
        deferUntilInteraction
        customPlayButton={useCircularPlayButton && <CircularPlayButton />}
        sx={{ borderRadius: "20px" }}
      />
    );
  } else if (headerImage) {
    mediaContent = (
      <Image
        src={headerImage.imageUrl ?? "/placeholder.jpg"}
        alt={headerImage.altText || "Header image"}
        width={400}
        height={250}
        style={{ width: "100%", height: "auto", borderRadius: "20px" }}
      />
    );
  }

  return (
    <Box
      className={`fluid-box${slotProps?.rootContainer?.removePatternBg ? "" : " pattern-bg"}`}
      component="section"
      data-track="visibility"
      data-parent-container-type="hero"
      data-module-name={dataModuleName}
      data-ranking-algorithm="manual"
      data-module-ranking-position={resolvedRanking}
    >
      <Box
        className="destination-page-container"
        data-testid={`${testId}-page-container`}
        sx={{
          padding: {
            xs: padding?.xs ?? "4rem 2.4rem",
            sm: padding?.mobile ?? "4rem 2.4rem",
            md: padding?.tablet ?? "6rem clamp(2.4rem, 4.5vw, 6.5rem)",
            lg: padding?.desktop ?? "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
          },
          position: "relative",
          zIndex: 2
        }}
      >
        <Box
          sx={{
            backgroundColor: "transparent",
            width: "100%",
            color: "text.primary"
          }}
        >
          <Box
            sx={deepmerge({
              display: "flex",
              alignItems: "flex-start",
              gap: {
                xs: "4rem",
                md: "6rem",
                lg: "8rem"
              },
              alignSelf: "stretch",
              flexDirection: {
                xs: "column",
                md: "column",
                lg: mediaLeft ? "row-reverse" : "row"
              },
              justifyContent: {
                xs: "flex-start",
                md: "space-between",
                lg: "space-between"
              }
            }, slotProps?.contentContainer?.sx ?? {})}
          >
            {/* Text Content - Left on desktop, top on mobile */}
            <Box
              sx={deepmerge({
                textAlign: "left",
                flex: { xs: "1 1 auto", tablet: "1 1 55%" },
                width: "100%",
                maxWidth: { lg: "580px" }
              }, slotProps?.textContent?.sx ?? {})}
            >
              <Box
                sx={deepmerge({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0,
                  alignSelf: "stretch"
                }, slotProps?.innerTextContainer?.sx ?? {})}
              >
                {/* H1 */}
                {primaryHeader && (
                  <Typography
                    variant={headerLevel ?? "h1"}
                    data-testid={`${testId}-page-header`}
                    sx={deepmerge({
                      color: "primary.main",
                      lineHeight: {
                        xs: "130%",
                        md: "120%",
                        lg: "120%"
                      },
                      marginBottom: "4rem",
                      alignSelf: "stretch"
                    }, slotProps?.heroHeader?.sx ?? {})}
                  >
                    {primaryHeader}
                  </Typography>
                )}
               {secondaryHeader && (
                 <SectionHeader
                    headerText={secondaryHeader}
                    headerTestId={`${testId}-hero-header`}
                    headerSx={{
                      textTransform: "uppercase",
                      fontSize: { xs: "2.4rem", sm: "2.6rem", md: "2.8rem" }
                    }}
                    sx={{marginBottom: "1.2rem"}}
                    headerLevel="h3"
                  />
               )}
                 <Typography
                    component="div"
                    variant="h2"
                    data-testid={`${testId}-hero-copy`}
                    sx={{fontSize:{xs:"1.4rem", sm: "1.8rem"},lineHeight:"2.7rem",fontWeight:"500","& p": { margin: 0 },marginBottom:"2.4rem",...slotProps?.sectionHeader?.copySx}}
                  >
                   {renderCopyText()}
                  </Typography>

                
                <Stack
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                    gap: "2.4rem",
                    alignItems: "flex-start",
                    flexWrap: "wrap"
                  }}
                >
                  {multipleDynamicCtas && multipleDynamicCtas.map((cta, index) => {
                    const variant = index % 2 === 0 ? "outlined" : "contained";
                    return (
                      <DynamicCtaButton
                        key={cta.label}
                        label={cta.label ?? ""}
                        ctaType={cta.ctaType}
                        ctaData={cta.ctaData}
                        testId={`landing-page-hero_${index}`}
                        variant={cta.variant ?? variant}
                        color={cta.color ?? "primary"}
                        targetIdSuffix="hero"
                      />
                    );
                  })}
                  {singleDynamicCta && (
                    <DynamicCtaButton
                      label={singleDynamicCta.label}
                      ctaType={singleDynamicCta.ctaType}
                      ctaData={singleDynamicCta.ctaData}
                      testId={`${testId}-hero-cta-button`}
                      variant={singleDynamicCta.variant ?? "outlined"}
                      color={singleDynamicCta.color ?? "primary"}
                    />
                  )}
                </Stack>
                {isSmFeefoSummary && feefoSummary && (
                  <FeefoSummarySection isSmFeefoSummary summary={feefoSummary} />
                )}
              </Box>
            </Box>
            {/* Media Section - Right on desktop, bottom on mobile */}
            {hasMedia && (
              <Box
                sx={deepmerge({
                  position: "relative",
                  width: { xs: "100%", tablet: "100%" },
                  maxWidth: {
                    xs: "100%",
                    lg: "580px"
                  },
                  minWidth: { tablet: "340px" }
                }, slotProps?.mediaContainer?.sx ?? {})}
                data-testid={`wistia-${testId}`}
              >
                {mediaContent}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PageHero;
